import { useEffect, useState } from 'react';
import { getLabelById } from '../utilities/getLabelById';
/**
 * useInformationProtection is a custom hook which is initialized with
 * the information protection service. The hook returns the
 *  - Applied sensitivity label
 *  - All labels
 *  - Current label i.e. the label information from all labels matching the currently applied label id
 *  - Parent label i.e. the label information from all labels of the parent label if applicable
 * The hook also refreshes the data whenever the sensitivity label changes.
 * @param informationProtectionService The information protection service
 * @returns applied sensitivity label, information protection labels, current label, parent label
 */ export function useInformationProtection(informationProtectionService, resolvedUrl) {
    const [appliedSensitivityLabel, setAppliedSensitivityLabel] = useState();
    const [sensitivityLabels, setSensitivityLabels] = useState();
    const [currentLabel, setCurrentLabel] = useState();
    const [parentLabel, setParentLabel] = useState();
    const [isDowngradeJustificationRequired, setIsDowngradeJustificationRequired] = useState(false);
    const [moreInfoUrl, setMoreInfoUrl] = useState();
    const [supportsCLP, setSupportsCLP] = useState(false);
    const [canWrite, setCanWrite] = useState(false);
    // Retrieve & refresh applied label on sensitivityLabelChanged event
    useEffect(()=>{
        const updateAppliedLabel = async ()=>{
            const appliedLabel = await informationProtectionService.getCurrentLabel(resolvedUrl);
            setAppliedSensitivityLabel(appliedLabel);
        };
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        updateAppliedLabel();
        informationProtectionService.on('sensitivityLabelChanged', updateAppliedLabel);
        return ()=>{
            informationProtectionService.off('sensitivityLabelChanged', updateAppliedLabel);
        };
    }, [
        informationProtectionService,
        resolvedUrl
    ]);
    // Retrieve all labels
    useEffect(()=>{
        // TODO: Need to implement a 4 hour timer to refresh all labels
        const getSensitivityLabelInfo = async ()=>{
            const labels = await informationProtectionService.getLabels();
            setSensitivityLabels(labels);
        };
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getSensitivityLabelInfo();
    }, [
        informationProtectionService
    ]);
    // Retrieve current & parent label
    useEffect(()=>{
        if (appliedSensitivityLabel && sensitivityLabels) {
            const label = getLabelById(sensitivityLabels, appliedSensitivityLabel.sensitivityLabelId);
            setCurrentLabel(label);
            if (label?.parent) {
                setParentLabel(label.parent);
            } else {
                setParentLabel(undefined);
            }
        } else {
            setCurrentLabel(undefined);
            setParentLabel(undefined);
        }
    }, [
        appliedSensitivityLabel,
        sensitivityLabels
    ]);
    // Retrieve policy settings
    useEffect(()=>{
        // TODO: Need to implement a 4 hour timer to refresh all labels
        const getPolicySettings = async ()=>{
            const policySettings = await informationProtectionService.getPolicySettings();
            if (policySettings) {
                setIsDowngradeJustificationRequired(policySettings.isDowngradeJustificationRequired);
                setMoreInfoUrl(policySettings.moreInfoUrl);
            }
        };
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getPolicySettings();
    }, [
        informationProtectionService
    ]);
    // Retrieve IRM Capabilities
    useEffect(()=>{
        // TODO: Need to implement a 4 hour timer to refresh all labels
        const getIrmInformation = async ()=>{
            const capabilities = await informationProtectionService.getIrmInformation(resolvedUrl);
            if (capabilities) {
                setSupportsCLP(capabilities.supportsCLP);
                setCanWrite(capabilities.canWrite);
            }
        };
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getIrmInformation();
    }, [
        informationProtectionService,
        resolvedUrl,
        appliedSensitivityLabel
    ]);
    return {
        appliedSensitivityLabel,
        sensitivityLabels,
        currentLabel,
        parentLabel,
        isDowngradeJustificationRequired,
        moreInfoUrl,
        supportsCLP,
        canWrite
    };
}
